import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { ChevronRightIcon, CloseIcon } from "@pkg/icons";
import { cn } from "@/utils/cn";

const bannerVariants = cva(
  cn(
    "relative flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1",
    "px-2 py-3 text-center font-archivo text-xs font-normal xs:text-sm sm:px-4 sm:text-base md:px-10 lg:px-20",
    "bg-(--banner-bg) text-(--banner-fg)",
    "[&_strong]:font-bold [&_strong]:text-(--banner-accent)",
  ),
  {
    variants: {
      variant: {
        info: cn(
          "[--banner-bg:var(--banner-info-bg)] [--banner-fg:var(--banner-info-fg)]",
          "[--banner-accent:var(--banner-info-accent)] [--banner-action:var(--text-1)]",
        ),
        neutral: cn(
          "[--banner-bg:var(--banner-neutral-bg)] [--banner-fg:var(--banner-neutral-fg)]",
          "[--banner-accent:var(--text-1)] [--banner-action:var(--text-1)]",
        ),
        success: cn(
          "[--banner-bg:var(--success-secondary)] [--banner-fg:var(--banner-success-fg)]",
          "[--banner-accent:var(--success-primary)] [--banner-action:var(--success-primary)]",
        ),
        warning: cn(
          "[--banner-bg:var(--banner-warning-bg)] [--banner-fg:var(--banner-warning-fg)]",
          "[--banner-accent:var(--banner-warning-accent)] [--banner-action:var(--banner-warning-action)]",
        ),
        danger: cn(
          "[--banner-bg:var(--banner-danger-bg)] [--banner-fg:var(--banner-danger-fg)]",
          "[--banner-accent:var(--error-primary)] [--banner-action:var(--banner-danger-action)]",
        ),
      },
      dismissible: {
        true: "pr-10 md:pr-12",
        false: "",
      },
    },
    defaultVariants: {
      variant: "info",
      dismissible: false,
    },
  },
);

export type BannerVariant = NonNullable<
  VariantProps<typeof bannerVariants>["variant"]
>;

export interface BannerAction {
  label: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
}

export interface BannerProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bannerVariants> {
  icon?: React.ReactNode;
  action?: BannerAction;
  onDismiss?: () => void;
}

/**
 * Emphasised inline text. Renders a `<strong>`, so a bare `<strong>` in the
 * message gets the same treatment — this is the explicit spelling of it.
 */
const BannerAccent = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => (
  <strong className={className} {...props}>
    {children}
  </strong>
);
BannerAccent.displayName = "Banner.Accent";

const actionClassName = cn(
  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap",
  "font-semibold capitalize underline underline-offset-2",
  "text-(--banner-action) transition-opacity hover:opacity-80",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
);

const BannerRoot = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      variant,
      dismissible,
      icon,
      action,
      onDismiss,
      role = "status",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [dismissed, setDismissed] = React.useState(false);

    if (dismissed) return null;

    const handleDismiss = () => {
      setDismissed(true);
      onDismiss?.();
    };

    const actionContent = (
      <>
        {action?.label}
        <ChevronRightIcon className="size-4 shrink-0" />
      </>
    );

    return (
      <div
        ref={ref}
        data-component="Banner"
        role={role}
        className={cn(bannerVariants({ variant, dismissible, className }))}
        {...props}
      >
        {icon}
        <span className="text-balance">{children}</span>

        {action &&
          (action.href ? (
            <a
              href={action.href}
              onClick={action.onClick}
              className={actionClassName}
              {...(action.external && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
            >
              {actionContent}
            </a>
          ) : (
            <button
              type="button"
              onClick={action.onClick}
              className={actionClassName}
            >
              {actionContent}
            </button>
          ))}

        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className={cn(
              "absolute top-1/2 right-3 -translate-y-1/2 md:right-5",
              "rounded-full p-1 text-(--banner-action) opacity-60",
              "transition-opacity hover:opacity-100",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
            )}
          >
            <CloseIcon className="size-3.5" />
          </button>
        )}
      </div>
    );
  },
);
BannerRoot.displayName = "Banner";

const Banner = Object.assign(BannerRoot, { Accent: BannerAccent });

export default Banner;
